import React, {
  createContext,
  ReactElement,
  useCallback,
  useContext,
} from 'react';
import {
  SlideOver,
  SlideOverProvider,
  useSliderStates,
} from '@plerionhq/design-system';
import type {
  ContextualViewFunc,
  ContextualViewFuncParams,
  ContextualViewSlideOver,
  SlideOverProps,
} from '@plerionhq/design-system';
import { ContextualView } from './ContextualView';

interface Props {
  mfaId?: string;
  children: React.ReactElement;
}

export type SlideOverFuncProps = Partial<
  Omit<SlideOverProps, 'isOpen' | 'setIsOpen'>
> & {
  id: string;
};
export type SlideOverFunc = (params: SlideOverFuncProps) => void;

export const SlideOverContext = createContext<{
  contextualView: ContextualViewFunc;
}>({
  contextualView: () => {
    throw new Error('Not implemented');
  },
});

export function ContextualViewProvider({
  children,
  mfaId,
}: Props): ReactElement {
  const { deleteAllSlideOvers, deleteSlideOver, slideOver, slideOvers } =
    useSliderStates();

  const contextualView = useCallback(
    (params: ContextualViewFuncParams) => {
      return slideOver({ ...params });
    },
    [slideOver],
  );

  return (
    <SlideOverContext.Provider value={{ contextualView }}>
      <SlideOverProvider
        contextualView={contextualView}
        closeAllSlideOvers={deleteAllSlideOvers}
      >
        <>
          {children}
          {(slideOvers as unknown as ContextualViewSlideOver)?.map(
            ({
              isOpen,
              id,
              renderKey,
              style,
              overlay,
              disableCloseOnOverlayClick,
              slideOverClassName,
              ...viewProps
            }) => {
              slideOverClassName = `${slideOverClassName} ${id} contextual-view-slide-over max-w-[85vw] h-full bg-white dark:bg-gray-900 rounded-tl-3xl overflow-hidden bg-lightest-gray`;
              return (
                <SlideOver
                  key={renderKey}
                  setIsOpen={() => deleteSlideOver(id)}
                  {...{
                    mfaId: mfaId || viewProps.mfaId,
                    isOpen,
                    overlay,
                    disableCloseOnOverlayClick,
                    slideOverClassName,
                    style,
                  }}
                >
                  <style>{`.contextual-view-slide-over .slide-over-close { display: none !important; }`}</style>
                  <ContextualView
                    {...viewProps}
                    close={() => deleteSlideOver(id)}
                  />
                </SlideOver>
              );
            },
          )}
        </>
      </SlideOverProvider>
    </SlideOverContext.Provider>
  );
}

export const useContextualView = () => {
  const context = useContext(SlideOverContext);
  if (context === undefined) {
    throw new Error('SlideOver context is undefined');
  }
  return context;
};

// eslint-disable-next-line import/no-default-export
export default ContextualViewProvider;
